import {
  alpha,
  Box,
  Divider,
  Drawer,
  Hidden,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Typography,
  useTheme,
} from "@mui/material";
import { ChevronLeft, ChevronRight } from "@mui/icons-material";
import { useCallback, useMemo, useState } from "react";
import { navigationConfig } from "config/navigation";
import { Link, useLocation } from "react-router-dom";
import LogoIcon from "components/utils/LogoIcon";
import { SidebarState, toggleFolded, toggleOpen } from "store/features/sidebar";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "store";
import { useTranslation } from "react-i18next";
import { AuthState } from "store/features/auth";

const Sidebar: React.FC = () => {
  const { t, i18n } = useTranslation();
  const { role } = useSelector<RootState, AuthState>((store) => store.auth);
  const [lng, setLng] = useState("en");
  i18n.on("languageChanged", (lng) => setLng(`${lng}@${Date.now()}`));
  i18n.on("loaded", () => setLng(`${lng.split("@")[0]}@${Date.now()}`));

  const { pathname } = useLocation();
  const dispatch = useDispatch();
  const { folded, open } = useSelector<RootState, SidebarState>(
    (state) => state.sidebar,
  );
  const theme = useTheme();
  const accentColor = theme.palette.primary.main;
  const isRtl = i18n.dir(i18n.language) === "rtl";
  const anchor = "left";

  const toggleSidebarFolded = useCallback(() => {
    dispatch(toggleFolded());
  }, [dispatch]);

  const navItems = useMemo(() => {
    return navigationConfig
      .filter(({ role: roles }) => !roles || roles.includes(role as string))
      .map(({ label, to, icon: ItemIcon }) => {
        const isActive = pathname === to || pathname.startsWith(`${to}/`);
        return (
          <ListItemButton
            key={to}
            component={Link}
            to={to as string}
            selected={isActive}
            sx={{
              borderRadius: folded ? 0 : 2,
              mx: folded ? 0 : 1,
              mb: 0.5,
              justifyContent: folded ? "center" : "flex-start",
              px: folded ? 0 : undefined,
              minHeight: folded ? 44 : undefined,
              color: isActive ? accentColor : "text.secondary",
              "&.Mui-selected": {
                bgcolor: alpha(accentColor, 0.08),
                color: accentColor,
                borderRight:
                  folded && !isRtl ? `3px solid ${accentColor}` : undefined,
                borderLeft:
                  folded && isRtl ? `3px solid ${accentColor}` : undefined,
                "&:hover": { bgcolor: alpha(accentColor, 0.12) },
                "& .MuiListItemIcon-root": { color: accentColor },
              },
              "&:hover": {
                bgcolor: alpha(accentColor, 0.04),
              },
            }}
          >
            {ItemIcon && (
              <ListItemIcon
                sx={{
                  minWidth: 36,
                  color: isActive ? accentColor : "text.disabled",
                }}
              >
                <ItemIcon />
              </ListItemIcon>
            )}
            {!folded && (
              <ListItemText
                primary={t(`${label}.label`)}
                primaryTypographyProps={{
                  variant: "body2",
                  fontSize: "0.8125rem",
                  fontWeight: isActive ? 600 : 400,
                  noWrap: true,
                }}
              />
            )}
          </ListItemButton>
        );
      });
  }, [t, pathname, role, accentColor, folded]);

  const CollapseIcon = isRtl
    ? folded
      ? ChevronLeft
      : ChevronRight
    : folded
      ? ChevronRight
      : ChevronLeft;

  const drawerContent = (
    <>
      <Box>
        <Toolbar
          sx={{
            px: "12px !important",
            justifyContent: folded ? "center" : "flex-start",
            gap: 1.5,
            minHeight: "56px !important",
          }}
        >
          <LogoIcon width="32px" height="32px" />
          {!folded && (
            <Typography variant="subtitle1" fontWeight={700} noWrap>
              {t("appName")}
            </Typography>
          )}
        </Toolbar>
        <List sx={{ px: 0.5, pt: 0.5 }}>{navItems}</List>
      </Box>
      <Divider />
      <Box display="flex" justifyContent="center" py={1}>
        <IconButton
          onClick={toggleSidebarFolded}
          size="small"
          sx={{
            color: "text.secondary",
            border: 1,
            borderColor: "divider",
            width: 28,
            height: 28,
            "&:hover": {
              bgcolor: alpha(accentColor, 0.08),
              borderColor: accentColor,
              color: accentColor,
            },
            transition: "all 0.2s",
          }}
        >
          <CollapseIcon sx={{ fontSize: 16 }} />
        </IconButton>
      </Box>
    </>
  );

  return (
    <>
      {/* Desktop */}
      <Hidden smDown>
        <Drawer
          variant="permanent"
          anchor={anchor}
          sx={{
            flexShrink: 0,
            "& .MuiDrawer-paper": {
              width: folded ? 56 : 240,
              boxSizing: "border-box",
              justifyContent: "space-between",
              transition: theme.transitions.create(["width"], {
                duration: theme.transitions.duration.standard,
              }),
              overflow: folded ? "hidden" : "auto",
              borderRight: isRtl ? 0 : 1,
              borderLeft: isRtl ? 1 : 0,
              borderColor: "divider",
              boxShadow: "none",
            },
          }}
        >
          {drawerContent}
        </Drawer>
      </Hidden>

      {/* Mobile */}
      <Hidden mdUp>
        <Drawer
          variant="temporary"
          anchor={anchor}
          open={open}
          onClose={() => dispatch(toggleOpen())}
          sx={{
            "& .MuiDrawer-paper": {
              width: { xs: "75vw", sm: 260 },
              maxWidth: 280,
              boxSizing: "border-box",
              justifyContent: "space-between",
            },
          }}
        >
          {drawerContent}
        </Drawer>
      </Hidden>
    </>
  );
};

export default Sidebar;
